// THE TASK SHEET IN AN AI ANSWER (workspace functions/assistantAppForms.js).
// The assistant proposes a task — a new one, or an existing task moved to
// another status, given to someone, re-prioritised. The answer's "Review and
// save" calls ticketsAiFormOpen, which opens the tickets' own detail sheet
// (TicketsDetailModal, mounted at the shell root) in create or edit mode,
// seeded with the proposal. Nothing is written until the member saves in the
// sheet; its save then reports the saved task back (the draft's `aiRequest`),
// so the answer shows it as a row that opens the task.

export function ticketsAiForms() {
  return {
    app: {
      id: 'system/tickets',
      navbar: [{ label: 'tickets.answer.title', route: '/tasks', icon: 'check' }],
      apps: [{ icon: 'check' }]
    },
    forms: [
      {
        id: 'ticket',
        title: 'tickets.aiForm.title',
        describe:
          'Create a task, or change one (recordId = the task id): title, description, status, priority, assignee, type, due date.',
        open: 'ticketsAiFormOpen',
        fields: {
          title: { type: 'string', label: 'tickets.aiForm.field.title' },
          body: { type: 'string', label: 'tickets.aiForm.field.body' },
          columnKey: {
            type: 'enum',
            values: [
              'backlog',
              'todo',
              'in_progress',
              'blocked',
              'ready_to_test',
              'review',
              'done'
            ],
            label: 'tickets.aiForm.field.status',
            // the tickets' own words for each status (the detail sheet's);
            // `labels` is the key the server reads — the shell's catalog
            // resolves these bundle keys to { en, ka } before it sends them
            labels: {
              backlog: 'tickets.detail.status.backlog',
              todo: 'tickets.detail.status.todo',
              in_progress: 'tickets.state.inProgress',
              blocked: 'tickets.state.blocked',
              ready_to_test: 'tickets.state.readyToTest',
              review: 'tickets.state.readyToReview',
              done: 'tickets.detail.status.done'
            }
          },
          priority: {
            type: 'enum',
            values: ['P0', 'P1', 'P2', 'P3'],
            label: 'tickets.aiForm.field.priority',
            labels: {
              P0: 'tickets.priority.option.p0',
              P1: 'tickets.priority.option.p1',
              P2: 'tickets.priority.option.p2',
              P3: 'tickets.priority.option.p3'
            }
          },
          assignee: {
            type: 'string',
            describe: "'me', a teammate name or an email",
            label: 'tickets.aiForm.field.assignee'
          },
          issueType: {
            type: 'enum',
            values: ['bug', 'task', 'user_story'],
            label: 'tickets.aiForm.field.type',
            labels: {
              bug: 'tickets.detail.type.bug',
              task: 'tickets.detail.type.task',
              user_story: 'tickets.detail.type.userStory'
            }
          },
          dueDate: { type: 'date', label: 'tickets.aiForm.field.due' }
        }
      }
    ]
  }
}

// (formId, { values, recordId, requestId }) → true when the sheet opened.
// An assignee NAME resolves through the roster; one nobody has is left out
// (the member picks in the sheet), never guessed.
export async function ticketsAiFormOpen(formId, args) {
  const el = this
  if (formId !== 'ticket') return false
  const root = el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const a = args && typeof args === 'object' ? args : {}
  const v = a.values && typeof a.values === 'object' ? a.values : {}
  const patch = {}
  for (const k of ['title', 'body', 'columnKey', 'priority', 'issueType', 'dueDate']) {
    if (v[k] != null && v[k] !== '') patch[k] = String(v[k])
  }
  const who = typeof v.assignee === 'string' ? v.assignee.trim() : ''
  if (who) {
    const low = who.toLowerCase()
    if (low === 'me' && root.currentUserEmail) patch.assigneeEmail = String(root.currentUserEmail)
    else if (low.includes('@')) patch.assigneeEmail = low
    else {
      const people = Array.isArray(root.people) ? root.people : []
      const hit = people.find((x) => {
        const name = String(
          (x && (x.name || [x.firstName, x.lastName].filter(Boolean).join(' '))) || ''
        ).toLowerCase()
        return name && (name === low || name.split(' ').includes(low))
      })
      if (hit && hit.email) patch.assigneeEmail = String(hit.email)
    }
  }
  const aiRequest = a.requestId ? String(a.requestId) : null
  if (a.recordId == null || a.recordId === '') {
    root.update({
      selectedTicket: { _new: true },
      ticketEditMode: false,
      ticketDraft: el.call('freshTicket', { ...patch, aiRequest })
    })
    return true
  }
  const id = String(a.recordId)
  const lists = [root.tickets, root.myTickets, root.recentTickets]
  let t = null
  for (const list of lists) {
    t = (Array.isArray(list) ? list : []).find((x) => x && String(x.id) === id) || null
    if (t) break
  }
  if (!t) {
    try {
      const svc = await el.call('getSDK').getService('tickets')
      const doc = await svc.get(id)
      const raw = doc && doc.data ? doc.data : doc
      t = raw ? el.call('normalizeTicketDoc', raw) : null
    } catch (_) {
      t = null
    }
  }
  if (!t) return false
  root.update({
    selectedTicket: t,
    ticketEditMode: true,
    // The edit sheet's own seed (TicketsDetailModal's Edit): every field it
    // shows and _save round-trips, then the proposal over it.
    ticketDraft: {
      title: t.title || '',
      body: t.body || '',
      columnKey: t.columnKey || t.column_key || 'backlog',
      priority: t.priority || '',
      assigneeEmail: t.assigneeEmail || t.assignee_email || '',
      issueType: t.issueType || (t.metadata && t.metadata.issueType) || '',
      severity: t.severity || (t.metadata && t.metadata.severity) || '',
      env: t.env || (t.metadata && t.metadata.env) || '',
      epicName: t.epicName || t.epic_name || '',
      storyPoints: t.storyPoints || (t.metadata && t.metadata.storyPoints) || '',
      dueDate: t.dueDate || '',
      acceptanceCriteria:
        t.acceptanceCriteria || (t.metadata && t.metadata.acceptanceCriteria) || '',
      stepsToReproduce: t.stepsToReproduce || (t.metadata && t.metadata.stepsToReproduce) || '',
      expectedResult: t.expectedResult || (t.metadata && t.metadata.expectedResult) || '',
      reporterEmail: t.reporterEmail || t.reporter_email || '',
      labels: t.labels || [],
      ...patch,
      aiRequest
    }
  })
  return true
}

// The sheet's save reports a task saved from an AI answer's form. Quiet
// without a request or outside the shell.
export function ticketsAiFormReport(requestId, t) {
  const el = this
  if (!requestId || !t || typeof el.context?.functions?.assistantFormSaved !== 'function')
    return false
  const id = t.id != null ? String(t.id) : ''
  return el.call('assistantFormSaved', requestId, {
    id,
    title: t.title || '',
    meta: el.call('stateLabelText', el, t.state || t.columnKey),
    route: id ? '/tasks?ticket=' + encodeURIComponent(id) : '/tasks'
  })
}
